import React, { useState, useEffect, useCallback, memo } from "react";
import { TextField, makeStyles } from "@material-ui/core";
import ButtonBB from "@View/components/buttons/ButtonBB";

// Стили для модалки - все инпуты без фона
const useStyles = makeStyles(() => ({
    textField: {
        "& .MuiOutlinedInput-root": {
            backgroundColor: "transparent",
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#54606F",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#54606F",
            },
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#E1E6F1",
        },
        "& .MuiInputBase-input": {
            fontSize: "14px",
            color: "#333",
        },
    },
}));

/**
 * Модальное окно редактирования ячейки.
 */
const EditCellModal = memo(({
    selectedCell,
    initialComment,
    onClose,
    onApply,
}) => {
    const classes = useStyles();
    const [newValue, setNewValue] = useState(selectedCell?.newValue || "");
    const [comment, setComment] = useState(initialComment || "");

    useEffect(() => {
        if (selectedCell) {
            setNewValue(selectedCell.newValue || "");
            setComment(initialComment || "");
        }
    }, [selectedCell, initialComment]);

    const handleApply = useCallback(() => {
        onApply({
            ...selectedCell,
            newValue,
            comment,
        });
    }, [selectedCell, newValue, comment, onApply]);

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    if (!selectedCell) return null;

    return (
        <>
            {/* Backdrop без затемнения */}
            <div
                style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 999,
                }}
                onClick={handleClose}
            />
            <div
                style={{
                    position: "fixed",
                    top: selectedCell.position?.top || 100,
                    left: selectedCell.position?.left || 100,
                    transform: "translateX(-50%)",
                    background: "#fff",
                    padding: "0",
                    boxShadow: "0 4px 24px rgba(0,0,0,0.12)",
                    borderRadius: "8px",
                    zIndex: 1000,
                    minWidth: "380px",
                    border: "1px solid #E1E6F1",
                    overflow: "visible",
                }}
            >
                {/* Стрелка вверх */}
                <div
                    style={{
                        position: "absolute",
                        top: "-6px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        width: 0,
                        height: 0,
                        borderLeft: "8px solid transparent",
                        borderRight: "8px solid transparent",
                        borderBottom: "8px solid #54606F",
                        zIndex: 1,
                    }}
                />

                {/* Header */}
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "12px 16px",
                        backgroundColor: "#54606F",
                        color: "#fff",
                        borderRadius: "8px 8px 0 0",
                    }}
                >
                    <div style={{
                        fontWeight: 600,
                        fontSize: "13px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                    }}>
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                        </svg>
                        Редактирование значения
                    </div>
                    <div
                        style={{
                            cursor: "pointer",
                            color: "#fff",
                            fontSize: "18px",
                            lineHeight: 1,
                            opacity: 0.8,
                            transition: "opacity 0.15s",
                        }}
                        onClick={handleClose}
                        onMouseEnter={(e) => e.currentTarget.style.opacity = "1"}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = "0.8"}
                    >
                        ×
                    </div>
                </div>

                {/* Content */}
                <div style={{ padding: "16px" }}>
                    {/* Название строки - просто текст */}
                    <div
                        style={{
                            fontSize: "13px",
                            color: "#54606F",
                            marginBottom: "14px",
                            fontWeight: 500,
                        }}
                    >
                        {selectedCell.rowLabel}
                    </div>

                    {/* Старое → Новое - одинаковые инпуты */}
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "16px",
                        }}
                    >
                        <TextField
                            size="small"
                            variant="outlined"
                            className={classes.textField}
                            style={{ flex: 1 }}
                            inputProps={{
                                style: {
                                    textAlign: "right",
                                    fontFamily: "monospace",
                                },
                                readOnly: true,
                            }}
                            value={selectedCell.oldValue}
                        />
                        <div style={{
                            color: "#54606F",
                            fontSize: "18px",
                            fontWeight: 500,
                            flexShrink: 0,
                        }}>→</div>
                        <TextField
                            size="small"
                            variant="outlined"
                            className={classes.textField}
                            style={{ flex: 1 }}
                            inputProps={{
                                style: {
                                    textAlign: "right",
                                    fontFamily: "monospace",
                                }
                            }}
                            value={newValue}
                            onChange={(e) => setNewValue(e.target.value)}
                            autoFocus
                        />
                    </div>

                    {/* Комментарий */}
                    <div style={{ marginBottom: "16px" }}>
                        <div style={{
                            fontSize: "12px",
                            color: "#8494AA",
                            marginBottom: "6px",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                            fontWeight: 500,
                        }}>
                            Комментарий
                        </div>
                        <TextField
                            multiline
                            rows={2}
                            fullWidth
                            variant="outlined"
                            size="small"
                            className={classes.textField}
                            placeholder="Добавьте комментарий..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        />
                    </div>

                    {/* Кнопка */}
                    <ButtonBB
                        style={{
                            width: "100%",
                            backgroundColor: "#54606F",
                            color: "#fff",
                            fontWeight: 500,
                        }}
                        onClick={handleApply}
                    >
                        Применить
                    </ButtonBB>
                </div>
            </div>
        </>
    );
});

EditCellModal.displayName = "EditCellModal";

export default EditCellModal;
